"use client";

import * as React from "react";
import { ACTIVE_ACCOUNTS, accountLabel } from "@/data/accounts";

type VatYearSetting = {
  taxYear: number;
  accountingMethod: "VEREINNAHMT" | "VEREINBART";
  vatScheme: "EFFEKTIV" | "PAUSCHAL";
  flatRateCode: string | null;
  flatRatePercent: number | null;
  locked: boolean;
  lockedAt: string | null;
  hasEntries: boolean;
};

type BaseCurrencyCode = "CHF" | "EUR" | "USD" | "GBP";

type BaseCurrencyYearSetting = {
  fiscalYear: number;
  baseCurrency: BaseCurrencyCode;
  locked: boolean;
  lockedAt: string | null;
  hasEntries: boolean;
};

type FxSource = "SNB" | "ESTV" | "ECB" | "MANUAL";
type FxScheduleKind = "DAYS_10" | "DAYS_30" | "MONTH_END";
type FxPolicy = {
  id: string;
  currencyCode: string;
  source: FxSource;
  scheduleKind: FxScheduleKind;
  active: boolean;
  lastRunAt: string | null;
  nextRunAt: string;
};

type CanonicalVatCode =
  | "STD_81"
  | "RED_26"
  | "LODGE_38"
  | "EXPORT_0"
  | "EXEMPT_0"
  | "INPUT_81"
  | "INPUT_26"
  | "INPUT_38"
  | "RC_81";
type VatCodeTxType = "SALE" | "PURCHASE" | "BOTH";
type VatCase = "INLAND" | "IMPORT" | "EXPORT" | "EXEMPT" | "OPTION";
type AmountType = "EXKL" | "INKL";
type VatCodeMapping = {
  code: string;
  label: string;
  canonicalCode: CanonicalVatCode;
  ratePercent: number;
  txType: VatCodeTxType;
  vatCaseDefault: VatCase;
  amountTypeDefault: AmountType;
  postingHint: string | null;
  bookingTextExample: string | null;
  active: boolean;
  system: boolean;
};

type VatRateVersion = {
  id: string;
  code: string;
  ratePercent: number;
  validFrom: string;
  validTo: string | null;
  active: boolean;
  sourceNote: string | null;
};

type AiMode = "ASSIST" | "REVIEW_REQUIRED" | "AUTO_POST_LOW_RISK";
type AiProfile = {
  countryCode: string;
  aiMode: AiMode;
  confidenceThreshold: number;
  autoPostEnabled: boolean;
  maxAutoPostAmountChf: number;
  requireReviewForImport: boolean;
  requireReviewForNewVendor: boolean;
};
type AiPolicyRule = {
  id: string;
  ruleKey: string;
  enabled: boolean;
  priority: number;
  configJson: Record<string, unknown>;
};

function inferCanonicalCode(code: string, fallback: CanonicalVatCode): CanonicalVatCode {
  const c = (code || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!c) return fallback;
  if (c.startsWith("IMPMAT") || c.startsWith("IMPINV") || c.startsWith("IMP") || c.startsWith("VDL")) return "RC_81";
  if (c.startsWith("UST81") || c.startsWith("ENT81")) return "STD_81";
  if (c.startsWith("UST26") || c.startsWith("ENT26")) return "RED_26";
  if (c.startsWith("UST38") || c.startsWith("ENT38")) return "LODGE_38";
  if (c.startsWith("EXP")) return "EXPORT_0";
  if (c.startsWith("VSM81") || c.startsWith("VSB81")) return "INPUT_81";
  if (c.startsWith("VSM26") || c.startsWith("VSB26")) return "INPUT_26";
  if (c.startsWith("VSM38") || c.startsWith("VSB38")) return "INPUT_38";
  if (
    c.startsWith("FREI") ||
    c.startsWith("AUSL") ||
    c.startsWith("MELD") ||
    c.startsWith("DI") ||
    c.startsWith("TAX") ||
    c.startsWith("GEMV") ||
    c.startsWith("VNEN") ||
    c.startsWith("SUBV") ||
    c.startsWith("SPEN")
  ) {
    return "EXEMPT_0";
  }
  return fallback;
}

const VAT_CODES_FALLBACK: VatCodeMapping[] = [
  { code: "VSM81EXKL", label: "Vorsteuer Material/DL 8.1 exkl.", canonicalCode: "INPUT_81", ratePercent: 8.1, txType: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL", postingHint: "1170", bookingTextExample: "Vorsteuer Material 8.1%", active: true, system: true },
  { code: "VSM81INKL", label: "Vorsteuer Material/DL 8.1 inkl.", canonicalCode: "INPUT_81", ratePercent: 8.1, txType: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL", postingHint: "1170", bookingTextExample: "Vorsteuer Material 8.1% (inkl.)", active: true, system: true },
  { code: "VSB81EXKL", label: "Vorsteuer Investitionen 8.1 exkl.", canonicalCode: "INPUT_81", ratePercent: 8.1, txType: "PURCHASE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL", postingHint: "1171", bookingTextExample: "Vorsteuer Investitionen 8.1%", active: true, system: true },
  { code: "UST81EXKL", label: "Umsatzsteuer Normalsatz exkl.", canonicalCode: "STD_81", ratePercent: 8.1, txType: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL", postingHint: "2200/2201", bookingTextExample: "Umsatzsteuer 8.1%", active: true, system: true },
  { code: "UST81INKL", label: "Umsatzsteuer Normalsatz inkl.", canonicalCode: "STD_81", ratePercent: 8.1, txType: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "INKL", postingHint: "2200/2201", bookingTextExample: "Umsatzsteuer 8.1% (inkl.)", active: true, system: true },
  { code: "UST26EXKL", label: "Umsatzsteuer reduziert exkl.", canonicalCode: "RED_26", ratePercent: 2.6, txType: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL", postingHint: "2200/2201", bookingTextExample: "Umsatzsteuer 2.6%", active: true, system: true },
  { code: "UST38EXKL", label: "Umsatzsteuer Beherbergung exkl.", canonicalCode: "LODGE_38", ratePercent: 3.8, txType: "SALE", vatCaseDefault: "INLAND", amountTypeDefault: "EXKL", postingHint: "2200/2201", bookingTextExample: "Umsatzsteuer Beherbergung 3.8%", active: true, system: true },
  { code: "EXP0", label: "Export 0%", canonicalCode: "EXPORT_0", ratePercent: 0, txType: "SALE", vatCaseDefault: "EXPORT", amountTypeDefault: "EXKL", postingHint: "-", bookingTextExample: "Export 0% mit Ausfuhrnachweis", active: true, system: true }
];

function displayCode(code: string): string {
  return code
    .replace(/EXKL$/i, "exkl")
    .replace(/INKL$/i, "inkl");
}

function parsePostingHint(value: string | null | undefined): { primary: string; secondary: string } {
  const parts = String(value || "")
    .split(/[\/,+]/)
    .map((x) => x.trim())
    .filter(Boolean);
  return {
    primary: parts[0] || "",
    secondary: parts[1] || ""
  };
}

function normalizeSinglePostingHint(value: string | null | undefined): string {
  const parsed = parsePostingHint(value);
  if (ALLOWED_POSTING_HINT_ACCOUNTS.has(parsed.primary)) return parsed.primary;
  if (ALLOWED_POSTING_HINT_ACCOUNTS.has(parsed.secondary)) return parsed.secondary;
  return parsed.primary;
}

const ALLOWED_POSTING_HINT_ACCOUNTS = new Set(["1170", "1171", "1172", "2201"]);

export default function VatSettingsScreen() {
  const postingHintAccounts = React.useMemo(
    () => ACTIVE_ACCOUNTS.filter((a) => ALLOWED_POSTING_HINT_ACCOUNTS.has(a.number)),
    []
  );

  const [taxYear, setTaxYear] = React.useState<number>(new Date().getFullYear());
  const [accountingMethod, setAccountingMethod] = React.useState<"VEREINNAHMT" | "VEREINBART">("VEREINBART");
  const [vatScheme, setVatScheme] = React.useState<"EFFEKTIV" | "PAUSCHAL">("EFFEKTIV");
  const [flatRateCode, setFlatRateCode] = React.useState("SSS");
  const [flatRatePercent, setFlatRatePercent] = React.useState("6.5");
  const [locked, setLocked] = React.useState(false);
  const [baseCurrency, setBaseCurrency] = React.useState<BaseCurrencyCode>("CHF");
  const [baseCurrencyLoaded, setBaseCurrencyLoaded] = React.useState<BaseCurrencyCode>("CHF");
  const [baseCurrencyLocked, setBaseCurrencyLocked] = React.useState(false);
  const [fxCurrencyCode, setFxCurrencyCode] = React.useState<BaseCurrencyCode>("EUR");
  const [fxSource, setFxSource] = React.useState<FxSource>("ECB");
  const [fxScheduleKind, setFxScheduleKind] = React.useState<FxScheduleKind>("DAYS_30");
  const [fxPolicies, setFxPolicies] = React.useState<FxPolicy[]>([]);
  const [vatCodes, setVatCodes] = React.useState<VatCodeMapping[]>([]);
  const [vatRateVersions, setVatRateVersions] = React.useState<VatRateVersion[]>([]);
  const [selectedVatCode, setSelectedVatCode] = React.useState("");
  const [vatCodeDropdownOpen, setVatCodeDropdownOpen] = React.useState(false);
  const [vcCode, setVcCode] = React.useState("VSM81EXKL");
  const [vcLabel, setVcLabel] = React.useState("Vorsteuer Material/DL 8.1 exkl.");
  const [vcCanonical, setVcCanonical] = React.useState<CanonicalVatCode>("INPUT_81");
  const [vcRate, setVcRate] = React.useState("8.1");
  const [vcTx, setVcTx] = React.useState<VatCodeTxType>("PURCHASE");
  const [vcCase, setVcCase] = React.useState<VatCase>("INLAND");
  const [vcPostingHint, setVcPostingHint] = React.useState("1170");
  const [vcPostingPrimary, setVcPostingPrimary] = React.useState("1170");
  const [vcBookingTextExample, setVcBookingTextExample] = React.useState("Vorsteuer Material 8.1%");
  const [rvCode, setRvCode] = React.useState("VSM81EXKL");
  const [rvRatePercent, setRvRatePercent] = React.useState("8.1");
  const [rvValidFrom, setRvValidFrom] = React.useState(`${new Date().getFullYear()}-01-01`);
  const [rvValidTo, setRvValidTo] = React.useState("");
  const [rvSourceNote, setRvSourceNote] = React.useState("");
  const [aiMode, setAiMode] = React.useState<AiMode>("REVIEW_REQUIRED");
  const [aiCountryCode, setAiCountryCode] = React.useState("CH");
  const [aiConfidenceThreshold, setAiConfidenceThreshold] = React.useState("0.85");
  const [aiAutoPostEnabled, setAiAutoPostEnabled] = React.useState(false);
  const [aiMaxAutoPostAmountChf, setAiMaxAutoPostAmountChf] = React.useState("500");
  const [aiRequireReviewForImport, setAiRequireReviewForImport] = React.useState(true);
  const [aiRequireReviewForNewVendor, setAiRequireReviewForNewVendor] = React.useState(true);
  const [aiPolicyRules, setAiPolicyRules] = React.useState<AiPolicyRule[]>([]);
  const [aiRuleKey, setAiRuleKey] = React.useState("high_amount_review");
  const [aiRuleEnabled, setAiRuleEnabled] = React.useState(true);
  const [aiRulePriority, setAiRulePriority] = React.useState("100");
  const [aiRuleConfigJson, setAiRuleConfigJson] = React.useState("{\"amountChf\": 2000}");
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState<string | null>(null);
  const vatCodeDropdownRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!vatCodeDropdownRef.current) return;
      if (!vatCodeDropdownRef.current.contains(e.target as Node)) {
        setVatCodeDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function applyVatCodeToEditor(c: VatCodeMapping) {
    setVcCode(c.code);
    setVcLabel(c.label);
    setVcCanonical(c.canonicalCode);
    setVcRate(String(c.ratePercent));
    setVcTx(c.txType);
    setVcCase(c.vatCaseDefault);
    const postingPrimary = normalizeSinglePostingHint(c.postingHint || "");
    setVcPostingPrimary(postingPrimary);
    setVcPostingHint(postingPrimary);
    setVcBookingTextExample(c.bookingTextExample || "");
  }

  React.useEffect(() => {
    setVcPostingHint(vcPostingPrimary);
  }, [vcPostingPrimary]);

  async function loadFxPolicies() {
    const res = await fetch("/api/fx/policies", { cache: "no-store" });
    const data = (await res.json()) as { ok?: boolean; policies?: FxPolicy[]; error?: string };
    if (!res.ok || !data.ok) throw new Error(data.error || "FX-Policies konnten nicht geladen werden");
    setFxPolicies(data.policies || []);
  }

  async function loadVatCodes() {
    const res = await fetch("/api/vat/codes", { cache: "no-store" });
    const data = (await res.json()) as { ok?: boolean; items?: VatCodeMapping[]; error?: string };
    const items = res.ok && data.ok && Array.isArray(data.items) && data.items.length > 0 ? data.items : VAT_CODES_FALLBACK;
    setVatCodes(items);
    if (items.length > 0 && !selectedVatCode) {
      setSelectedVatCode(items[0].code);
      applyVatCodeToEditor(items[0]);
    }
  }

  async function loadVatRateVersions(code?: string) {
    const query = code ? `?code=${encodeURIComponent(code)}` : "";
    const res = await fetch(`/api/vat/rates${query}`, { cache: "no-store" });
    const data = (await res.json()) as { ok?: boolean; items?: VatRateVersion[]; error?: string };
    if (!res.ok || !data.ok) throw new Error(data.error || "MWST-Satz-Versionen konnten nicht geladen werden");
    setVatRateVersions(Array.isArray(data.items) ? data.items : []);
  }

  async function loadAiProfile() {
    const res = await fetch("/api/ai/profile", { cache: "no-store" });
    const data = (await res.json()) as { ok?: boolean; profile?: AiProfile; error?: string };
    if (!res.ok || !data.ok || !data.profile) throw new Error(data.error || "KI-Profil konnte nicht geladen werden");
    const p = data.profile;
    setAiCountryCode((p.countryCode || "CH").toUpperCase());
    setAiMode(p.aiMode);
    setAiConfidenceThreshold(String(p.confidenceThreshold));
    setAiAutoPostEnabled(Boolean(p.autoPostEnabled));
    setAiMaxAutoPostAmountChf(String(p.maxAutoPostAmountChf));
    setAiRequireReviewForImport(Boolean(p.requireReviewForImport));
    setAiRequireReviewForNewVendor(Boolean(p.requireReviewForNewVendor));
  }

  async function loadAiPolicyRules() {
    const res = await fetch("/api/ai/policies", { cache: "no-store" });
    const data = (await res.json()) as { ok?: boolean; items?: AiPolicyRule[]; error?: string };
    if (!res.ok || !data.ok) throw new Error(data.error || "KI-Policy-Regeln konnten nicht geladen werden");
    setAiPolicyRules(Array.isArray(data.items) ? data.items : []);
  }

  async function loadYear(year: number) {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch(`/api/vat/year-settings?taxYear=${year}`, { cache: "no-store" });
      const data = (await res.json()) as { ok?: boolean; setting?: VatYearSetting | null; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "MWST-Einstellungen konnten nicht geladen werden");
      const s = data.setting;
      if (!s) {
        setAccountingMethod("VEREINBART");
        setVatScheme("EFFEKTIV");
        setFlatRateCode("SSS");
        setFlatRatePercent("6.5");
        setLocked(false);
        setNote("Noch keine Einstellung für dieses Jahr. Bitte jetzt festlegen.");
      } else {
        setAccountingMethod(s.accountingMethod);
        setVatScheme(s.vatScheme);
        setFlatRateCode(s.flatRateCode || "SSS");
        setFlatRatePercent(s.flatRatePercent != null ? String(s.flatRatePercent) : "6.5");
        setLocked(Boolean(s.locked || s.hasEntries));
        setNote(s.hasEntries ? "Für dieses Jahr existieren bereits Buchungen. Änderung ist gesperrt." : "Einstellung geladen.");
      }

      try {
        const curRes = await fetch(`/api/currency/year-settings?fiscalYear=${year}`, { cache: "no-store" });
        const curData = (await curRes.json()) as {
          ok?: boolean;
          setting?: BaseCurrencyYearSetting | null;
        };
        if (!curRes.ok || !curData.ok || !curData.setting) {
          setBaseCurrency("CHF");
          setBaseCurrencyLoaded("CHF");
          setBaseCurrencyLocked(false);
        } else {
          setBaseCurrency(curData.setting.baseCurrency);
          setBaseCurrencyLoaded(curData.setting.baseCurrency);
          setBaseCurrencyLocked(Boolean(curData.setting.locked || curData.setting.hasEntries));
        }
      } catch {
        setBaseCurrency("CHF");
        setBaseCurrencyLoaded("CHF");
        setBaseCurrencyLocked(false);
      }

      try {
        await loadFxPolicies();
      } catch {
        setFxPolicies([]);
      }
      await loadVatCodes();
      await loadVatRateVersions();
      await loadAiProfile();
      await loadAiPolicyRules();
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Laden");
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => {
    loadYear(taxYear);
  }, [taxYear]);

  async function save() {
    setBusy(true);
    setNote(null);
    try {
      const payload: {
        taxYear: number;
        accountingMethod: "VEREINNAHMT" | "VEREINBART";
        vatScheme: "EFFEKTIV" | "PAUSCHAL";
        flatRateCode?: string;
        flatRatePercent?: number;
      } = {
        taxYear,
        accountingMethod,
        vatScheme
      };
      if (vatScheme === "PAUSCHAL") {
        payload.flatRateCode = flatRateCode;
        payload.flatRatePercent = Number(flatRatePercent);
      }
      const res = await fetch("/api/vat/year-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "Speichern fehlgeschlagen");
      setNote("✅ MWST-Einstellung gespeichert.");
      await loadYear(taxYear);
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  }

  async function saveBaseCurrency() {
    setBusy(true);
    setNote(null);
    try {
      let confirmChangeStep1 = false;
      let confirmChangeStep2 = false;
      if (baseCurrency !== baseCurrencyLoaded) {
        confirmChangeStep1 = window.confirm(
          `Hauswährung für ${taxYear} wirklich von ${baseCurrencyLoaded} auf ${baseCurrency} ändern?`
        );
        if (!confirmChangeStep1) {
          setNote("Änderung abgebrochen (Bestätigung 1/2).");
          return;
        }
        confirmChangeStep2 = window.confirm(
          "Bitte nochmals bestätigen (2/2): Änderung ist nur zum Fiskaljahresanfang sinnvoll und nach erster Buchung gesperrt."
        );
        if (!confirmChangeStep2) {
          setNote("Änderung abgebrochen (Bestätigung 2/2).");
          return;
        }
      }

      const res = await fetch("/api/currency/year-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fiscalYear: taxYear,
          baseCurrency,
          confirmChangeStep1,
          confirmChangeStep2
        })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "Speichern Hauswährung fehlgeschlagen");
      setNote("✅ Hauswährung gespeichert.");
      await loadYear(taxYear);
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Speichern Hauswährung");
    } finally {
      setBusy(false);
    }
  }

  async function saveFxPolicy() {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/fx/policies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currencyCode: fxCurrencyCode,
          source: fxSource,
          scheduleKind: fxScheduleKind,
          active: true
        })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "FX-Policy speichern fehlgeschlagen");
      await loadFxPolicies();
      setNote("✅ FX-Policy gespeichert.");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Speichern FX-Policy");
    } finally {
      setBusy(false);
    }
  }

  async function runDueFxPolicies() {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/fx/policies/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      const data = (await res.json()) as {
        ok?: boolean;
        error?: string;
        result?: { checked: number; done: number; failed: number };
      };
      if (!res.ok || !data.ok) throw new Error(data.error || "FX-Run fehlgeschlagen");
      const r = data.result || { checked: 0, done: 0, failed: 0 };
      await loadFxPolicies();
      setNote(`✅ FX-Run fertig: geprüft ${r.checked}, erfolgreich ${r.done}, Fehler ${r.failed}.`);
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim FX-Run");
    } finally {
      setBusy(false);
    }
  }

  async function saveVatCode() {
    setBusy(true);
    setNote(null);
    try {
      const derivedAmountType: AmountType = vcCode.toUpperCase().endsWith("INKL") ? "INKL" : "EXKL";
      const derivedCanonical = inferCanonicalCode(vcCode, vcCanonical);
      const res = await fetch("/api/vat/codes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: vcCode,
          label: vcLabel,
          canonicalCode: derivedCanonical,
          ratePercent: Number(vcRate),
          txType: vcTx,
          vatCaseDefault: vcCase,
          amountTypeDefault: derivedAmountType,
          postingHint: vcPostingHint,
          bookingTextExample: vcBookingTextExample,
          active: true
        })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "MWST-Code speichern fehlgeschlagen");
      setSelectedVatCode(vcCode.toUpperCase());
      await loadVatCodes();
      setNote("✅ MWST-Code gespeichert.");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Speichern MWST-Code");
    } finally {
      setBusy(false);
    }
  }

  async function saveVatRateVersion() {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/vat/rates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: rvCode,
          ratePercent: Number(rvRatePercent),
          validFrom: rvValidFrom,
          validTo: rvValidTo || null,
          active: true,
          sourceNote: rvSourceNote || null
        })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "MWST-Satz-Version speichern fehlgeschlagen");
      await loadVatRateVersions();
      setNote("✅ MWST-Satz-Version gespeichert.");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Speichern MWST-Satz-Version");
    } finally {
      setBusy(false);
    }
  }

  async function deleteVatRateVersion(id: string) {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch(`/api/vat/rates?id=${encodeURIComponent(id)}`, { method: "DELETE" });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "Löschen MWST-Satz-Version fehlgeschlagen");
      await loadVatRateVersions();
      setNote("✅ MWST-Satz-Version gelöscht.");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Löschen MWST-Satz-Version");
    } finally {
      setBusy(false);
    }
  }

  async function saveAiProfile() {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/ai/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          countryCode: aiCountryCode,
          aiMode,
          confidenceThreshold: Number(aiConfidenceThreshold),
          autoPostEnabled: aiAutoPostEnabled,
          maxAutoPostAmountChf: Number(aiMaxAutoPostAmountChf),
          requireReviewForImport: aiRequireReviewForImport,
          requireReviewForNewVendor: aiRequireReviewForNewVendor
        })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "KI-Profil speichern fehlgeschlagen");
      await loadAiProfile();
      setNote("✅ KI-Profil gespeichert.");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Speichern KI-Profil");
    } finally {
      setBusy(false);
    }
  }

  async function saveAiPolicyRule() {
    setBusy(true);
    setNote(null);
    try {
      let cfg: Record<string, unknown> = {};
      const txt = aiRuleConfigJson.trim();
      if (txt) cfg = JSON.parse(txt) as Record<string, unknown>;
      const res = await fetch("/api/ai/policies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ruleKey: aiRuleKey,
          enabled: aiRuleEnabled,
          priority: Number(aiRulePriority),
          configJson: cfg
        })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "KI-Policy-Regel speichern fehlgeschlagen");
      await loadAiPolicyRules();
      setNote("✅ KI-Policy-Regel gespeichert.");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Speichern KI-Policy-Regel");
    } finally {
      setBusy(false);
    }
  }

  async function deleteAiPolicyRule(id: string) {
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch(`/api/ai/policies?id=${encodeURIComponent(id)}`, { method: "DELETE" });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error || "KI-Policy-Regel löschen fehlgeschlagen");
      await loadAiPolicyRules();
      setNote("✅ KI-Policy-Regel gelöscht.");
    } catch (err) {
      setNote(err instanceof Error ? `❌ ${err.message}` : "❌ Fehler beim Löschen KI-Policy-Regel");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>MWST-Setup (CH)</h1>
          <p>Abrechnungsart und Verfahren pro Jahr festlegen. Nach erster Buchung im Jahr ist die Einstellung gesperrt.</p>
        </div>
      </div>

      <div className="table-wrap" style={{ padding: 14, maxWidth: 900 }}>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
          <label style={{ display: "contents" }}>
            <span>Steuerjahr</span>
            <input
              type="number"
              className="input"
              value={taxYear}
              onChange={(e) => setTaxYear(Number(e.target.value) || new Date().getFullYear())}
              min={2000}
              max={2100}
              disabled={busy}
            />
          </label>

          <label style={{ display: "contents" }}>
            <span>Abrechnungsart</span>
            <select
              className="select"
              value={accountingMethod}
              onChange={(e) => setAccountingMethod(e.target.value as "VEREINNAHMT" | "VEREINBART")}
              disabled={busy || locked}
            >
              <option value="VEREINNAHMT">Vereinnahmt</option>
              <option value="VEREINBART">Vereinbart</option>
            </select>
          </label>

          <label style={{ display: "contents" }}>
            <span>Verfahren</span>
            <select
              className="select"
              value={vatScheme}
              onChange={(e) => setVatScheme(e.target.value as "EFFEKTIV" | "PAUSCHAL")}
              disabled={busy || locked}
            >
              <option value="EFFEKTIV">Effektiv</option>
              <option value="PAUSCHAL">Pauschal</option>
            </select>
          </label>

          {vatScheme === "PAUSCHAL" && (
            <>
              <label style={{ display: "contents" }}>
                <span>Pauschal-Code</span>
                <input
                  className="input"
                  value={flatRateCode}
                  onChange={(e) => setFlatRateCode(e.target.value)}
                  disabled={busy || locked}
                />
              </label>
              <label style={{ display: "contents" }}>
                <span>Pauschal-Satz (%)</span>
                <input
                  className="input"
                  value={flatRatePercent}
                  onChange={(e) => setFlatRatePercent(e.target.value)}
                  disabled={busy || locked}
                />
              </label>
            </>
          )}
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn btn-primary" type="button" disabled={busy || locked} onClick={save}>
            {busy ? "Speichern..." : "Jahres-Setup speichern"}
          </button>
          {locked && <span className="pill pill-provisional">Gesperrt (Buchungen vorhanden)</span>}
        </div>

        <hr style={{ margin: "16px 0", border: "none", borderTop: "1px solid var(--border)" }} />

        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
          <label style={{ display: "contents" }}>
            <span>Hauswährung (ab 01.01.{taxYear})</span>
            <select
              className="select"
              value={baseCurrency}
              onChange={(e) => setBaseCurrency(e.target.value as BaseCurrencyCode)}
              disabled={busy || baseCurrencyLocked}
            >
              <option value="CHF">CHF</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </label>
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn btn-primary" type="button" disabled={busy || baseCurrencyLocked} onClick={saveBaseCurrency}>
            {busy ? "Speichern..." : "Hauswährung speichern"}
          </button>
          {baseCurrencyLocked && <span className="pill pill-provisional">Hauswährung gesperrt (Buchungen vorhanden)</span>}
        </div>

        <hr style={{ margin: "16px 0", border: "none", borderTop: "1px solid var(--border)" }} />

        <div style={{ fontWeight: 700, marginBottom: 10 }}>FX-Automatik (Kurse nachführen)</div>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
          <label style={{ display: "contents" }}>
            <span>Währung</span>
            <select className="select" value={fxCurrencyCode} onChange={(e) => setFxCurrencyCode(e.target.value as BaseCurrencyCode)} disabled={busy}>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="CHF">CHF</option>
            </select>
          </label>
          <label style={{ display: "contents" }}>
            <span>Kursquelle</span>
            <select className="select" value={fxSource} onChange={(e) => setFxSource(e.target.value as FxSource)} disabled={busy}>
              <option value="SNB">SNB</option>
              <option value="ESTV">ESTV</option>
              <option value="ECB">ECB</option>
              <option value="MANUAL">MANUAL</option>
            </select>
          </label>
          <label style={{ display: "contents" }}>
            <span>Intervall</span>
            <select className="select" value={fxScheduleKind} onChange={(e) => setFxScheduleKind(e.target.value as FxScheduleKind)} disabled={busy}>
              <option value="DAYS_10">Alle 10 Tage</option>
              <option value="DAYS_30">Alle 30 Tage</option>
              <option value="MONTH_END">Monatsende</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={saveFxPolicy}>
            {busy ? "Speichern..." : "FX-Policy speichern"}
          </button>
          <button className="btn btn-ghost" type="button" disabled={busy} onClick={runDueFxPolicies}>
            {busy ? "Läuft..." : "Fällige FX-Updates jetzt ausführen"}
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          {fxPolicies.length === 0 ? (
            <div style={{ color: "var(--muted)" }}>Noch keine FX-Policies hinterlegt.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Währung</th>
                  <th>Quelle</th>
                  <th>Intervall</th>
                  <th>Nächster Lauf</th>
                  <th>Letzter Lauf</th>
                </tr>
              </thead>
              <tbody>
                {fxPolicies.map((p) => (
                  <tr key={p.id}>
                    <td>{p.currencyCode}</td>
                    <td>{p.source}</td>
                    <td>{p.scheduleKind}</td>
                    <td>{new Date(p.nextRunAt).toLocaleString("de-CH")}</td>
                    <td>{p.lastRunAt ? new Date(p.lastRunAt).toLocaleString("de-CH") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <hr style={{ margin: "16px 0", border: "none", borderTop: "1px solid var(--border)" }} />
        <div style={{ fontWeight: 700, marginBottom: 10 }}>MWST-Kürzel & Mapping</div>
        <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 8 }}>
          Alle Felder sind auf Deutsch. <strong>Canonical Code</strong> ist nur die interne technische Regel (z. B. Normalsatz, Vorsteuer, Bezugsteuer).
          <br />
          <strong>inkl./exkl.</strong> wird automatisch aus dem Kürzel erkannt (Suffix <code>...INKL</code> oder <code>...EXKL</code>).
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
          <label style={{ display: "contents" }}>
            <span>Kürzel</span>
            <div ref={vatCodeDropdownRef} style={{ position: "relative" }}>
              <button
                className="select"
                type="button"
                disabled={busy || vatCodes.length === 0}
                onClick={() => setVatCodeDropdownOpen((v) => !v)}
                style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <span>
                  {vatCodes.length === 0
                    ? "(keine Kürzel vorhanden)"
                    : (() => {
                        const cur = vatCodes.find((x) => x.code === (selectedVatCode || vcCode));
                        return cur ? `${displayCode(cur.code)} · ${cur.label}` : "(Kürzel wählen)";
                      })()}
                </span>
                <span aria-hidden="true" style={{ marginLeft: 8 }}>{vatCodeDropdownOpen ? "▴" : "▾"}</span>
              </button>
              {vatCodeDropdownOpen && vatCodes.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 40,
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    maxHeight: 280,
                    overflowY: "auto",
                    background: "#fff",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    boxShadow: "0 6px 20px rgba(2, 10, 24, 0.12)"
                  }}
                >
                  {vatCodes.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => {
                        setSelectedVatCode(c.code);
                        applyVatCodeToEditor(c);
                        setVatCodeDropdownOpen(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        border: "none",
                        borderBottom: "1px solid var(--border)",
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 13
                      }}
                    >
                      {displayCode(c.code)} · {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
          <label style={{ display: "contents" }}>
            <span>Neues Kürzel (optional)</span>
            <input className="input" value={vcCode} onChange={(e) => setVcCode(e.target.value.toUpperCase())} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Bezeichnung</span>
            <input className="input" value={vcLabel} onChange={(e) => setVcLabel(e.target.value)} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Zielkonto / Buchungshinweis</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              <select
                className="select"
                value={vcPostingPrimary}
                onChange={(e) => setVcPostingPrimary(e.target.value)}
                disabled={busy}
              >
                <option value="">(kein Zielkonto)</option>
                {postingHintAccounts.map((a) => (
                  <option key={a.number} value={a.number}>
                    {accountLabel(a)}
                  </option>
                ))}
              </select>
            </div>
          </label>
          <label style={{ display: "contents" }}>
            <span>Buchungstext-Beispiel</span>
            <input className="input" value={vcBookingTextExample} onChange={(e) => setVcBookingTextExample(e.target.value)} disabled={busy} placeholder="z.B. Vorsteuer Material 8.1%" />
          </label>
          <label style={{ display: "contents" }}>
            <span>Satz %</span>
            <input className="input" value={vcRate} onChange={(e) => setVcRate(e.target.value)} disabled={busy} />
          </label>
        </div>
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={saveVatCode}>
            {busy ? "Speichern..." : "MWST-Kürzel speichern"}
          </button>
        </div>
        <div style={{ marginTop: 8, color: "var(--muted)", fontSize: 12 }}>
          Kürzel werden nach dem Speichern still aktualisiert und nur im Auswahlfeld geführt.
        </div>

        <hr style={{ margin: "16px 0", border: "none", borderTop: "1px solid var(--border)" }} />
        <div style={{ fontWeight: 700, marginBottom: 10 }}>MWST-Satz-Versionen (gültig ab/bis)</div>
        <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 8 }}>
          Hier pflegst du historische und zukünftige Sätze pro Kürzel. Diese Logik gilt auch für Pauschal-Codes.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
          <label style={{ display: "contents" }}>
            <span>Kürzel</span>
            <input
              className="input"
              value={rvCode}
              onChange={(e) => setRvCode(e.target.value.toUpperCase())}
              list="vat-rate-code-options"
              disabled={busy}
            />
          </label>
          <datalist id="vat-rate-code-options">
            {Array.from(new Set([...vatCodes.map((c) => c.code), flatRateCode ? flatRateCode.toUpperCase() : ""]))
              .filter(Boolean)
              .map((c) => (
                <option key={c} value={c} />
              ))}
          </datalist>
          <label style={{ display: "contents" }}>
            <span>Satz %</span>
            <input className="input" value={rvRatePercent} onChange={(e) => setRvRatePercent(e.target.value)} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Gültig ab</span>
            <input className="input" type="date" value={rvValidFrom} onChange={(e) => setRvValidFrom(e.target.value)} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Gültig bis (optional)</span>
            <input className="input" type="date" value={rvValidTo} onChange={(e) => setRvValidTo(e.target.value)} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Hinweis / Quelle (optional)</span>
            <input className="input" value={rvSourceNote} onChange={(e) => setRvSourceNote(e.target.value)} disabled={busy} />
          </label>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={saveVatRateVersion}>
            {busy ? "Speichern..." : "MWST-Satz-Version speichern"}
          </button>
          <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => loadVatRateVersions(rvCode || undefined)}>
            Für Kürzel laden
          </button>
          <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => loadVatRateVersions()}>
            Alle laden
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          {vatRateVersions.length === 0 ? (
            <div style={{ color: "var(--muted)" }}>Noch keine MWST-Satz-Versionen hinterlegt.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Kürzel</th>
                  <th>Satz %</th>
                  <th>Gültig ab</th>
                  <th>Gültig bis</th>
                  <th>Hinweis</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {vatRateVersions.map((v) => (
                  <tr key={v.id}>
                    <td>{displayCode(v.code)}</td>
                    <td>{v.ratePercent.toFixed(3).replace(/\.?0+$/, "")}</td>
                    <td>{v.validFrom}</td>
                    <td>{v.validTo || "-"}</td>
                    <td>{v.sourceNote || "-"}</td>
                    <td>
                      <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => deleteVatRateVersion(v.id)}>
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <hr style={{ margin: "16px 0", border: "none", borderTop: "1px solid var(--border)" }} />
        <div style={{ fontWeight: 700, marginBottom: 10 }}>KI-Profil (Hybrid pro Mandat)</div>
        <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 8 }}>
          Globales KI-Modell bleibt zentral, dieses Profil steuert das Verhalten für dieses Mandat.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
          <label style={{ display: "contents" }}>
            <span>Land (ISO-2)</span>
            <input className="input" value={aiCountryCode} onChange={(e) => setAiCountryCode(e.target.value.toUpperCase())} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>KI-Modus</span>
            <select className="select" value={aiMode} onChange={(e) => setAiMode(e.target.value as AiMode)} disabled={busy}>
              <option value="ASSIST">ASSIST (nur Vorschlag)</option>
              <option value="REVIEW_REQUIRED">REVIEW_REQUIRED (Freigabe nötig)</option>
              <option value="AUTO_POST_LOW_RISK">AUTO_POST_LOW_RISK</option>
            </select>
          </label>
          <label style={{ display: "contents" }}>
            <span>Confidence-Schwelle (0..1)</span>
            <input className="input" value={aiConfidenceThreshold} onChange={(e) => setAiConfidenceThreshold(e.target.value)} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Auto-Post aktiviert</span>
            <select className="select" value={aiAutoPostEnabled ? "yes" : "no"} onChange={(e) => setAiAutoPostEnabled(e.target.value === "yes")} disabled={busy}>
              <option value="no">Nein</option>
              <option value="yes">Ja</option>
            </select>
          </label>
          <label style={{ display: "contents" }}>
            <span>Max Auto-Post Betrag CHF</span>
            <input className="input" value={aiMaxAutoPostAmountChf} onChange={(e) => setAiMaxAutoPostAmountChf(e.target.value)} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Import immer Review</span>
            <select className="select" value={aiRequireReviewForImport ? "yes" : "no"} onChange={(e) => setAiRequireReviewForImport(e.target.value === "yes")} disabled={busy}>
              <option value="yes">Ja</option>
              <option value="no">Nein</option>
            </select>
          </label>
          <label style={{ display: "contents" }}>
            <span>Neuer Lieferant immer Review</span>
            <select className="select" value={aiRequireReviewForNewVendor ? "yes" : "no"} onChange={(e) => setAiRequireReviewForNewVendor(e.target.value === "yes")} disabled={busy}>
              <option value="yes">Ja</option>
              <option value="no">Nein</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={saveAiProfile}>
            {busy ? "Speichern..." : "KI-Profil speichern"}
          </button>
        </div>

        <div style={{ marginTop: 14, fontWeight: 700, marginBottom: 8 }}>KI-Policy-Regeln</div>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 12 }}>
          <label style={{ display: "contents" }}>
            <span>Rule Key</span>
            <input className="input" value={aiRuleKey} onChange={(e) => setAiRuleKey(e.target.value.toLowerCase())} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Aktiv</span>
            <select className="select" value={aiRuleEnabled ? "yes" : "no"} onChange={(e) => setAiRuleEnabled(e.target.value === "yes")} disabled={busy}>
              <option value="yes">Ja</option>
              <option value="no">Nein</option>
            </select>
          </label>
          <label style={{ display: "contents" }}>
            <span>Priorität</span>
            <input className="input" value={aiRulePriority} onChange={(e) => setAiRulePriority(e.target.value)} disabled={busy} />
          </label>
          <label style={{ display: "contents" }}>
            <span>Config JSON</span>
            <input className="input" value={aiRuleConfigJson} onChange={(e) => setAiRuleConfigJson(e.target.value)} disabled={busy} />
          </label>
        </div>
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={saveAiPolicyRule}>
            {busy ? "Speichern..." : "KI-Policy-Regel speichern"}
          </button>
        </div>
        <div style={{ marginTop: 12 }}>
          {aiPolicyRules.length === 0 ? (
            <div style={{ color: "var(--muted)" }}>Noch keine KI-Policy-Regeln hinterlegt.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Aktiv</th>
                  <th>Priorität</th>
                  <th>Config</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {aiPolicyRules.map((r) => (
                  <tr key={r.id}>
                    <td>{r.ruleKey}</td>
                    <td>{r.enabled ? "Ja" : "Nein"}</td>
                    <td>{r.priority}</td>
                    <td><code>{JSON.stringify(r.configJson)}</code></td>
                    <td>
                      <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => deleteAiPolicyRule(r.id)}>
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {note && <div style={{ marginTop: 10, color: "var(--muted)" }}>{note}</div>}
      </div>
    </div>
  );
}
